import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { YesNoEnum } from '../HouseholdOutgoings/types';
import { ApplicationFormPageEnum } from '../types';
import { partnerAddressHistoryQuestions } from './questions';
import { IPartnerAddressHistoryForm } from './types';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';
import { getCurrentRoute } from '@utils/location';
import { useEffect } from 'react';

export const PartnerAddressHistory = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const formApplicantNames = searchParams.get('applicantNames')?.split(',') || [];
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IPartnerAddressHistoryForm>({
    defaultValues: {
      sameAddress: '',
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IPartnerAddressHistoryForm) => {
    const deleteFollowingPages = formattedResponse && formattedResponse.sameAddress !== formData.sameAddress;
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: partnerAddressHistoryQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId, deleteFollowingPages });

    const newActiveAppicant = form.getValues('sameAddress') === YesNoEnum.YES ? '1' : '2';
    const nextPage =
      form.getValues('sameAddress') === YesNoEnum.YES
        ? ApplicationFormPageEnum.INTRODUCTION
        : ApplicationFormPageEnum.ADDRESS_HISTORY;

    searchParams.set('activeApplicant', newActiveAppicant);
    searchParams.set('section', nextPage);
    setSearchParams(searchParams);
  };
  const isNextStepDisabled = !form.formState.isDirty;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div>
          <FormattedMessage
            id={partnerAddressHistoryQuestions.sameAddress.labelKey}
            tagName={'h4'}
            values={{ applicant1: formApplicantNames[0], applicant2: formApplicantNames[1] }}
          />
          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'sameAddress'}
            radioOptions={partnerAddressHistoryQuestions.sameAddress.options}
            radioGroupProps={{ className: 'mt-2' }}
          />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isNextStepDisabled}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
